<template>
  <div>
    <!-- [Added by E2logy]: shown on "Finish route" while survey photos are still
    saved on the device. The route can only be finished once every photo has
    reached the server. -->
    <!-- [Modified by E2logy]: "Finish anyway" no longer finishes with photos
    still on the device. It sends the remaining photos one by one and finishes
    the route only when all of them are sent. -->
    <modal
      name="modal-pending-photos-finish"
      :width="`90%`"
      :maxWidth="600"
      :adaptive="true"
      :height="`auto`"
      :clickToClose="false"
      scrollable
    >
      <v-card>
        <v-card-text class="pt-4">
          <span class="text-h5">{{ $t("pending_photos_title") }}</span>
        </v-card-text>

        <v-card-text>
          <p>
            {{ $t("pending_photos_finish_message", { count: pendingCount }) }}
          </p>

          <b-alert show variant="info">
            {{ $t("pending_photos_network_hint") }}
          </b-alert>

          <b-alert show variant="warning" v-if="isOfflineNow">
            {{ $t("pending_photos_offline") }}
          </b-alert>

          <!-- Indeterminate while it waits for an upload that was already
          running, the count shows once the remaining photos are being sent. -->
          <v-progress-linear
            v-if="isSending"
            :value="progressValue"
            :indeterminate="!flush.isRunning"
            height="20"
            color="primary"
            striped
          >
            <strong v-if="flush.isRunning">
              {{
                $t("pending_photos_progress", {
                  sent: flush.sent,
                  total: flush.total
                })
              }}
            </strong>
          </v-progress-linear>

          <b-alert show variant="danger" v-else-if="notSentCount > 0">
            {{ $t("pending_photos_not_sent", { count: notSentCount }) }}
          </b-alert>
        </v-card-text>

        <v-card-actions class="d-flex flex-wrap justify-content-end">
          <b-button
            variant="outline-secondary"
            class="m-1"
            :disabled="isSending"
            @click="hideModal"
          >
            {{ $t("cancel") }}
          </b-button>
          <b-button
            variant="success"
            class="m-1"
            :disabled="isSending"
            @click="onClickFinishAnyway"
          >
            <b-spinner v-if="isSending" small></b-spinner>
            {{ $t("pending_photos_finish_anyway") }}
          </b-button>
        </v-card-actions>
      </v-card>
    </modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import PhotoQueueService from "@/services/photoQueueService";
import { logInfo } from "@/utils";

export default {
  name: "DialogPendingPhotosFinish",
  data: function() {
    return {
      notSentCount: 0,
      isNavigatorOffline: false,
      // True from the tap until the send ends, including the wait for an
      // upload that was already running.
      isSending: false
    };
  },
  computed: {
    ...mapGetters("myroute", ["isOffline"]),
    flush() {
      return PhotoQueueService.state.flush;
    },
    pendingCount() {
      return (
        PhotoQueueService.state.pendingCount +
        PhotoQueueService.state.pendingPortalCount
      );
    },
    progressValue() {
      if (!this.flush.total) return 0;
      return Math.round((this.flush.sent / this.flush.total) * 100);
    },
    isOfflineNow() {
      return this.isOffline || this.isNavigatorOffline;
    }
  },
  methods: {
    showModal() {
      this.notSentCount = 0;
      this.isNavigatorOffline = navigator.onLine === false;
      this.$modal.show("modal-pending-photos-finish");
    },
    hideModal() {
      this.$modal.hide("modal-pending-photos-finish");
    },
    // Sends every remaining photo one by one. An upload that is already running
    // is not stopped, flushAll waits for it and then carries on with the rest.
    // The route is finished only when nothing is left on the device.
    async onClickFinishAnyway() {
      this.notSentCount = 0;
      this.isSending = true;
      const { remaining } = await PhotoQueueService.flushAll();
      this.isSending = false;
      this.isNavigatorOffline = navigator.onLine === false;
      if (remaining === 0) {
        logInfo(this.$t("pending_photos_all_sent"));
        this.hideModal();
        this.$emit("continueFinish");
        return;
      }
      this.notSentCount = remaining;
    }
  }
};
</script>
